import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { useEffect } from 'react';
import { NavBar } from '../components/NavBar';

interface Section {
  id: string;
  title: string;
  icon: string;
  body: Array<{ kind: 'p' | 'h' | 'list' | 'note' | 'kbd'; text?: string; items?: string[] }>;
}

const SECTIONS: Section[] = [
  {
    id: 'workflow',
    title: 'Полный пайплайн',
    icon: '🔄',
    body: [
      { kind: 'p', text: 'Система превращает идею в опубликованное видео за 5 шагов:' },
      { kind: 'list', items: [
        '📋 Темы — что снимать (название + платформы + язык)',
        '✍️ Скрипты — AI пишет текст по теме (15-30 сек)',
        '🎬 Видео — HeyGen генерирует с твоим аватаром (5-10 мин)',
        '📅 Публикация — на одобренную дату/время',
        '📊 Аналитика — метрики через 6 часов после публикации',
      ]},
      { kind: 'note', text: 'На каждом шаге ты решаешь, идти дальше или вернуться. Ничего не делается автоматически без твоего одобрения.' },
    ],
  },
  {
    id: 'keys',
    title: 'API-ключи',
    icon: '🔐',
    body: [
      { kind: 'p', text: 'Минимум для работы — два ключа: OpenAI и HeyGen. Brave Search опционален.' },
      { kind: 'h', text: 'OpenAI' },
      { kind: 'p', text: 'platform.openai.com → API keys → Create. Используется для: генерация скриптов (gpt-4o-mini), эмбеддинги базы знаний, синтез веб-поиска, извлечение хуков конкурентов. Стоимость на 100 видео ≈ $0.50-1.50.' },
      { kind: 'h', text: 'HeyGen' },
      { kind: 'p', text: 'app.heygen.com → Account → API. Нужен для генерации видео. Каждое видео = 1-2 кредита HeyGen.' },
      { kind: 'h', text: 'Brave Search (опц.)' },
      { kind: 'p', text: 'brave.com/search/api → Free tier 2000 запросов/месяц. Нужен для веб-поиска в KB и поиска хуков конкурентов.' },
      { kind: 'note', text: 'Все ключи шифруются AES-256-GCM перед записью в БД. Plaintext нигде не логируется.' },
    ],
  },
  {
    id: 'topics',
    title: 'Темы',
    icon: '📋',
    body: [
      { kind: 'p', text: 'Тема — это короткое название будущего видео. AI расшифрует её в скрипт.' },
      { kind: 'h', text: 'Способы добавления' },
      { kind: 'list', items: [
        'Inline форма — одна тема за раз с настройками платформ/языка/CTA',
        'Массовый ввод — несколько тем построчно (используют общие настройки)',
        'API /topics/bulk — для импорта из CSV',
      ]},
      { kind: 'h', text: 'A/B тест' },
      { kind: 'p', text: 'Если включить — AI создаст 2 разных варианта скрипта (A и B) для одной темы. Полезно для тестирования подачи.' },
      { kind: 'h', text: 'Глобальный CTA' },
      { kind: 'p', text: 'Призыв к действию, который применяется ко всем темам без своего CTA. Например: «Подпишись, чтобы не пропустить новые видео».' },
    ],
  },
  {
    id: 'scripts',
    title: 'Скрипты',
    icon: '✍️',
    body: [
      { kind: 'p', text: 'AI генерирует скрипт по правилам: 60-90 сек = 150-200 слов, короткие предложения для lip-sync, без markdown, hook первым предложением, CTA в конце.' },
      { kind: 'h', text: 'Кнопки на скрипте' },
      { kind: 'list', items: [
        '✓ Одобрить — переводит в статус готов к видео',
        'Отклонить — с причиной, тема возвращается в draft',
        '🔄 Регенерировать — перегенерация с новым hook',
        '🎬 Создать видео — появляется ПОСЛЕ одобрения, отправляет в HeyGen',
        '💾 Сохранить — после прямого редактирования текста',
      ]},
      { kind: 'h', text: 'Хуки' },
      { kind: 'p', text: 'AI выбирает hook из библиотеки автоматически по правилу «наименее использованный → случайно». Чем больше хуков добавишь в /hooks — тем разнообразнее начало видео.' },
    ],
  },
  {
    id: 'videos',
    title: 'Видео',
    icon: '🎬',
    body: [
      { kind: 'p', text: 'HeyGen создаёт видео по одобренному скрипту. Время: 5-10 минут на видео.' },
      { kind: 'h', text: 'Aspect ratio автоматически' },
      { kind: 'list', items: [
        '9:16 — Instagram Reels, TikTok, YouTube Shorts, Telegram',
        '16:9 — LinkedIn, YouTube',
        '1:1 — Instagram Feed',
      ]},
      { kind: 'p', text: 'Если тема публикуется на нескольких платформах с разными форматами — создаётся несколько видео.' },
      { kind: 'h', text: 'Что если зависло' },
      { kind: 'p', text: 'Polling 2 минуты × 10 попыток = 20 минут максимум. После — статус timeout. Решение: «🔄 Регенерировать».' },
    ],
  },
  {
    id: 'publishing',
    title: 'Публикация',
    icon: '📅',
    body: [
      { kind: 'p', text: 'Поддерживается 5 платформ: Instagram, TikTok, YouTube, LinkedIn, Telegram.' },
      { kind: 'h', text: 'Telegram (самый простой)' },
      { kind: 'p', text: '/settings → Telegram → введи Bot Token + Channel ID. Бот должен быть админом канала.' },
      { kind: 'h', text: 'OAuth для остальных' },
      { kind: 'p', text: 'Для Instagram/YouTube/TikTok/LinkedIn нужны зарегистрированные dev-приложения в соответствующих кабинетах. Это технический шаг — заполняется один раз.' },
      { kind: 'note', text: 'В версии MVP реализован только Telegram. Реальные publishers для остальных — в следующих релизах.' },
    ],
  },
  {
    id: 'knowledge',
    title: 'База знаний',
    icon: '📚',
    body: [
      { kind: 'p', text: 'AI ссылается на загруженные тобой материалы при генерации скриптов. Без неё — генерирует общие фразы.' },
      { kind: 'h', text: '3 типа источников' },
      { kind: 'list', items: [
        'Документы — PDF/DOCX/TXT/MD до 50 МБ. AI-эмбеддинги текста',
        'Сайты — URL: одна страница или весь сайт (до 100 страниц)',
        'Веб-поиск — Brave найдёт + AI синтезирует единый док',
      ]},
      { kind: 'p', text: 'Поиск семантический (по смыслу, не по словам) — pgvector + cosine similarity. AI находит топ-5 релевантных чанков перед каждой генерацией скрипта.' },
    ],
  },
  {
    id: 'hooks',
    title: 'Библиотека хуков',
    icon: '🎯',
    body: [
      { kind: 'p', text: 'Hook — первая фраза видео, которая удерживает внимание. AI вставляет случайный hook в начало каждого скрипта.' },
      { kind: 'h', text: 'Источники хуков' },
      { kind: 'list', items: [
        'Системные — 3 предустановленных (Вопрос-провокация, Факт-шок, ...)',
        'Свои — добавляй вручную через форму',
        'Конкурентов — Brave + AI извлекают hook-фразы из топа поиска',
      ]},
      { kind: 'p', text: 'Логика выбора: ORDER BY usage_count ASC, RANDOM() — сначала наименее использованные. Так избегаем повторов.' },
    ],
  },
  {
    id: 'analytics',
    title: 'Аналитика',
    icon: '📊',
    body: [
      { kind: 'p', text: 'Метрики собираются автоматически каждые 6 часов через API платформ.' },
      { kind: 'list', items: [
        'Telegram — нет API метрик, пропускается',
        'Instagram — Graph API: views, likes, comments, reach',
        'YouTube — Data API: viewCount, likeCount, commentCount',
        'TikTok / LinkedIn — соответствующие API',
      ]},
      { kind: 'p', text: 'Дашборд показывает: 4 stat-карточки (всего/опубликовано/в очереди/ошибки), линия 30 дней, топ-5 видео по views, сравнение платформ. ER% = (likes+comments+shares)/reach × 100.' },
    ],
  },
  {
    id: 'troubleshoot',
    title: 'Что если…',
    icon: '🛠',
    body: [
      { kind: 'h', text: 'Кнопка «Сгенерировать скрипты» серая' },
      { kind: 'p', text: 'Нет тем со статусом draft. Добавь хотя бы одну тему через inline форму.' },
      { kind: 'h', text: 'Генерация падает с ошибкой' },
      { kind: 'p', text: 'Скорее всего — нет OpenAI ключа. Проверь /settings — должна быть зелёная плашка «✓ Проверен» на OpenAI.' },
      { kind: 'h', text: 'Видео не создаётся' },
      { kind: 'p', text: 'Нужен HeyGen ключ. На /settings проверь блок HeyGen.' },
      { kind: 'h', text: '422 от Brave' },
      { kind: 'p', text: 'Проверь активность ключа на brave.com/search/api/. Free tier 2000/мес.' },
      { kind: 'h', text: 'Скрипт начинается одинаково с другими' },
      { kind: 'p', text: 'Hook повторяется. Добавь больше хуков в /hooks (или подожди — каждый hook увеличивает usage_count, и менее использованные приоритетнее).' },
    ],
  },
];

export default function UserGuide() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [active, setActive] = useState('workflow');

  useEffect(() => {
    if (!user) navigate('/login', { replace: true });
  }, [user, navigate]);

  const section = SECTIONS.find((s) => s.id === active);

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Руководство 📖</h1>
          <p className="text-slate-500 mt-2">Подробное описание всех инструментов системы.</p>
        </header>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Side TOC */}
          <aside className="card-glass p-3 h-fit lg:sticky lg:top-6">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 mb-2">
              Содержание
            </div>
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-left transition ${
                    active === s.id
                      ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md'
                      : 'text-slate-600 hover:bg-slate-100/80'
                  }`}
                >
                  <span>{s.icon}</span>
                  <span className="font-medium">{s.title}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Body */}
          <article className="card-glass p-6 md:p-8">
            {section && (
              <>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                  <span className="text-3xl">{section.icon}</span>
                  <h2 className="text-2xl font-bold tracking-tight">{section.title}</h2>
                </div>
                <div className="space-y-4 text-slate-700 leading-relaxed">
                  {section.body.map((b, i) => {
                    if (b.kind === 'p') return <p key={i}>{b.text}</p>;
                    if (b.kind === 'h') return <h3 key={i} className="text-base font-semibold text-slate-900 pt-2">{b.text}</h3>;
                    if (b.kind === 'list') return (
                      <ul key={i} className="space-y-1.5 ml-1">
                        {b.items?.map((it, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <span className="text-indigo-500 mt-0.5">•</span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    );
                    if (b.kind === 'note') return (
                      <div key={i} className="rounded-2xl bg-indigo-50/60 border border-indigo-200/50 px-4 py-3 text-sm text-indigo-900 flex items-start gap-2">
                        <span>💡</span><span>{b.text}</span>
                      </div>
                    );
                    return null;
                  })}
                </div>

                <div className="flex gap-3 mt-8 pt-6 border-t border-slate-100">
                  <Link to="/onboarding" className="btn btn-md btn-ghost">🎓 Начало работы</Link>
                  <Link to="/topics" className="btn btn-md btn-primary ml-auto">→ К работе</Link>
                </div>
              </>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}
